import { createContext, useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";

// Every page has this provider but most visits never open a modal, so the
// dialog (Radix Dialog, focus trap, scroll lock) loads with the first one.
const Dialog = dynamic(
  () => import("@jammable/ui-components/Dialog").then((m) => m.Dialog),
  { ssr: false },
);
const DialogContent = dynamic(
  () => import("@jammable/ui-components/Dialog").then((m) => m.DialogContent),
  { ssr: false },
);

const ModalContext = createContext();

export const useModal = () => {
  return useContext(ModalContext);
};

// The paywall takes the whole screen on phone and desktop (design 7): the
// dialog box is a transparent, edge-to-edge scroller and UpgradeModal paints
// the dark page. Its close button sits on that dark backdrop.
const UPGRADE_FULLSCREEN =
  "!tw-p-0 !tw-gap-0 !tw-border-0 !tw-bg-transparent !tw-shadow-none !tw-rounded-none !tw-max-w-none !tw-w-screen md:!tw-w-screen md:!tw-min-w-0 !tw-h-[100dvh] !tw-mt-0 tw-overflow-y-auto";
const UPGRADE_CLOSE = (
  <span className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/[0.14] tw-text-white tw-transition-colors hover:tw-bg-white/25 md:tw-h-12 md:tw-w-12">
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  </span>
);

const ModalRenderer = ({ modal, closeModal }) => {
  const modalEntries = Object.entries(modal).filter(
    ([_, config]) => config.isOpen,
  );

  return (
    <>
      {modalEntries.map(([name, config], index) => {
        const isTopModal = index === modalEntries.length - 1;
        const opacity =
          name === "generate-image-modal" ||
          name === "marble-run-templates-modal" ||
          name === "video-template-grid"
            ? 1
            : modalEntries.length > 1 && !isTopModal
              ? 0.1
              : 1;

        return (
          <Dialog
            aria-hidden={false}
            open={true}
            // Keep `modal` STABLE per dialog instance. Radix renders different
            // component types for modal vs non-modal content, so toggling this
            // as the stack changes (e.g. a checkout drawer opening on top of
            // the upgrade modal) unmounts + remounts the underlying modal's
            // entire subtree. Cascade-dismiss is already prevented by the
            // `onOpenChange` guard below (it only closes the topmost modal).
            modal={true}
            key={name}
            preventClose={config.preventClose ?? name === "create-video-modal"}
            closeButton={config.closeButton ?? name === "create-video-modal"}
            onOpenChange={(e) => {
              if (e === false && isTopModal) {
                // config.onBeforeClose may keep it open (returns false), e.g.
                // the locked-cover paywall's "leave without your cover?" step.
                if (config.onBeforeClose?.() === false) return;
                closeModal(name);
              }
            }}
          >
            <DialogContent
              style={{ opacity: opacity > 0 ? opacity : 0.1 }}
              className={`${config.className ?? "md:tw-min-w-[450px] md:tw-max-w-lg"} ${name === "upgrade-modal" ? UPGRADE_FULLSCREEN : ""} tw-rounded-t-lg ${index === 1 && "tw-shadow-xl"}`}
              drawerMode={config.drawerMode ?? true}
              {...(name === "upgrade-modal"
                ? {
                    closeIcon: UPGRADE_CLOSE,
                    classNames: {
                      close:
                        "!tw-fixed !tw-right-4 !tw-top-4 md:!tw-right-8 md:!tw-top-7 tw-z-10",
                    },
                  }
                : {})}
              stackIndex={index}
              onCloseClick={() => {
                if (config.onBeforeClose?.() === false) return;
                closeModal(name);
              }}
              onPointerDownOutside={(e) => {
                if (!isTopModal) e.preventDefault();
              }}
              onInteractOutside={(e) => {
                if (!isTopModal) e.preventDefault();
              }}
              onEscapeKeyDown={(e) => {
                if (!isTopModal) e.preventDefault();
              }}
            >
              {config.content}
            </DialogContent>
          </Dialog>
        );
      })}
    </>
  );
};

export const ModalProvider = ({ children }) => {
  const [modal, setModal] = useState({});

  const openModal = (name, content, config = {}) => {
    setModal((prev) => {
      const { [name]: _, ...rest } = prev;
      return {
        ...rest,
        [name]: {
          isOpen: true,
          content,
          ...config,
        },
      };
    });
  };

  const closeModal = (name) => {
    setModal((prev) => ({ ...prev, [name]: { isOpen: false, content: null } }));
    document.body.style.overflow = "auto";
  };

  const closeAllModals = () => {
    setModal((prev) => {
      const newModalState = { ...prev };
      for (const name in newModalState) {
        newModalState[name].isOpen = false;
      }
      return newModalState;
    });
    document.body.style.overflow = "auto";
  };

  const onRouteChange = () => {
    closeAllModals();
  };

  const router = useRouter();
  useEffect(() => {
    router.events.on("routeChangeComplete", onRouteChange);

    return () => {
      router.events.off("routeChangeComplete", onRouteChange);
    };
  }, [modal]);

  const value = { modal, openModal, closeModal };

  return (
    <ModalContext.Provider value={value}>
      {children}
      <ModalRenderer modal={modal} closeModal={closeModal} />
    </ModalContext.Provider>
  );
};
